import { useForm } from '@tanstack/react-form';
import { HelpCircle } from '@untitled-ui/icons-react';
import { useEffect } from 'react';
import { z } from 'zod';

import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { TextField } from '../../../../../../components/inputs/text-field';
import { ToastVariant } from '../../../../../../components/toast';
import { Tooltip } from '../../../../../../components/tooltip';
import { shopifyExternalUrlSchema } from '../../../../../../entity/api/organization-integration';
import { addShopifyIntegrationReqSchema } from '../../../../../../entity/api/routes/organization';
import { BadRequestError } from '../../../../../../error';
import { convertError } from '../../../../../../error/convert';
import { trackError } from '../../../../../../error/tracking';
import { captureException } from '../../../../../../logger/capture-exception';
import { useAddShopifyIntegration } from '../../../../../../services/storage/organization-integrations-queries';
import { tGlobal, tNamespace } from '../../../../../../services/translation';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import { parseFirstZodError } from '../../../../../../shared/validation';

const t = tNamespace('app.integrations');

type ConnectShopifyModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
};

export function ConnectShopifyModal(props: ConnectShopifyModalProps) {
  const { open, onOpenChange, organizationId } = props;

  const addShopifyIntegration = useAddShopifyIntegration();
  const addToast = useToast();

  const form = useForm({
    defaultValues: {
      externalName: '',
      externalUrl: '',
      clientId: '',
      clientSecret: '',
    },
    onSubmit: function (data) {
      if (!data.formApi.state.isValid) {
        return;
      }

      addShopifyIntegration.mutate(
        {
          organizationId,
          data: {
            externalName: data.value.externalName ?? '',
            externalUrl: data.value.externalUrl ?? '',
            clientId: data.value.clientId ?? '',
            clientSecret: data.value.clientSecret ?? '',
          },
        },
        {
          onSuccess: function (result) {
            onOpenChange(false);
            form.reset();
            window.location.href = result.oauthUrl;
          },
          onError: function (err) {
            const handledErr = convertError(err);
            if (handledErr instanceof BadRequestError) {
              addToast({
                title: tGlobal('badRequest'),
                variant: ToastVariant.Error,
                id: Date.now(),
              });
              return;
            }

            captureException(
              err,
              {
                file: 'connect-shopify-modal.tsx',
                method: 'addShopifyIntegration',
                infoMessage: 'unable to add Shopify integration',
              },
              trackError,
              addToast
            );
          },
        }
      );
    },
  });

  useEffect(
    function () {
      if (!open) {
        form.reset();
      }
    },
    [open]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="min-w-[550px]">
        <div className="mb-6 flex w-full items-center justify-center">
          <div
            className={
              'flex size-[46px] shrink-0 items-center justify-center rounded-full bg-neutral-100'
            }
          >
            <img src="/shopify.svg" alt="Shopify" width={30} height={30} />
          </div>
        </div>
        <DialogTitle align="center">{t('connectShopify')}</DialogTitle>
        <DialogDescription align="center">{t('connectShopifyDescription')}</DialogDescription>
        <form className="mt-8 flex w-full flex-col gap-6">
          <form.Field
            name="externalName"
            validators={{
              onChange: (data) =>
                parseFirstZodError(
                  addShopifyIntegrationReqSchema.shape.body.shape.externalName,
                  data.value
                ),
              onBlur: (data) =>
                parseFirstZodError(
                  addShopifyIntegrationReqSchema.shape.body.shape.externalName,
                  data.value
                ),
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('integrationName')}</p>
                      <Tooltip text={t('integrationNameTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('integrationNamePlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <form.Field
            name="externalUrl"
            validators={{
              onChange: (data) => {
                return parseFirstZodError(
                  shopifyExternalUrlSchema as z.ZodType<string>,
                  data.value
                );
              },
              onBlur: (data) => {
                return parseFirstZodError(
                  shopifyExternalUrlSchema as z.ZodType<string>,
                  data.value
                );
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('myShopifyStore')}</p>
                      <Tooltip text={t('myShopifyStoreTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('myShopifyStorePlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <form.Field
            name="clientId"
            validators={{
              onChange: (data) => {
                return parseFirstZodError(z.string().min(1, 'FIELD_REQUIRED'), data.value);
              },
              onBlur: (data) => {
                return parseFirstZodError(z.string().min(1, 'FIELD_REQUIRED'), data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('shopifyClientId')}</p>
                      <Tooltip text={t('shopifyClientIdTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('shopifyClientIdPlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <form.Field
            name="clientSecret"
            validators={{
              onChange: (data) => {
                return parseFirstZodError(z.string().min(1, 'FIELD_REQUIRED'), data.value);
              },
              onBlur: (data) => {
                return parseFirstZodError(z.string().min(1, 'FIELD_REQUIRED'), data.value);
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  type="password"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('shopifyClientSecret')}</p>
                      <Tooltip text={t('shopifyClientSecretTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('shopifyClientSecretPlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
        </form>
        <DialogFooter>
          <Button
            variant="accent"
            size="xl"
            className="w-full"
            loading={addShopifyIntegration.isPending}
            onClick={() => void form.handleSubmit()}
          >
            {t('connect')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
